/**
 * 取引先マネージャーコンポーネント
 */

import { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Paper,
  Button,
  Text,
  Group,
  Loader,
  Accordion,
  Title,
  Box,
} from "@mantine/core";
import { showSuccess, showError } from "../utils/notifications";
import {
  Partner,
  PartnerPattern,
  fetchPartners,
  createPartner,
  fetchUnmappedPatterns,
  mapPatternToPartner,
} from "../services/partnerService";
import { Plus } from "lucide-react";
import { PartnerAccordionItem } from "./partner/PartnerAccordionItem";
import { UnmappedPatternsSection } from "./partner/UnmappedPatternsSection";
import { PartnerAddModal } from "./partner/PartnerAddModal";
import { PatternMapModal } from "./partner/PatternMapModal";

interface PartnerManagerProps {
  customerId: string;
}

export function PartnerManager({ customerId }: PartnerManagerProps) {
  // 取引先データ
  const [partners, setPartners] = useState<Partner[]>([]);
  const [unmappedPatterns, setUnmappedPatterns] = useState<PartnerPattern[]>([]);
  const [loading, setLoading] = useState(false);

  // モーダル状態
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mappingPattern, setMappingPattern] = useState<PartnerPattern | null>(null);

  // アコーディオン状態
  const [openedPartnerId, setOpenedPartnerId] = useState<string | null>(null);

  // データを読み込み
  const loadData = useCallback(
    async (silent = false) => {
      if (!customerId) return;

      if (!silent) setLoading(true);
      try {
        const [partnersData, patternsData] = await Promise.all([
          fetchPartners(customerId),
          fetchUnmappedPatterns(customerId),
        ]);
        setPartners(partnersData);
        setUnmappedPatterns(patternsData);
      } catch (error) {
        console.error(error);
        showError("データの取得に失敗しました");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [customerId]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 取引先を追加
  const handleAdd = async (officialName: string) => {
    if (!customerId) return;

    try {
      await createPartner(customerId, officialName);
      showSuccess("取引先を追加しました");
      await loadData();
    } catch (error) {
      showError(error instanceof Error ? error.message : "取引先の追加に失敗しました");
    }
  };

  // パターンを取引先に紐付け
  const handleMapPattern = async (patternId: string, partnerId: string) => {
    if (!customerId) return;

    try {
      await mapPatternToPartner(customerId, patternId, partnerId);
      showSuccess("パターンを紐付けました");
      loadData();
    } catch (error) {
      showError(error instanceof Error ? error.message : "パターンの紐付けに失敗しました");
    }
  };

  // 紐付けモーダルを開く
  const openMapModal = (pattern: PartnerPattern) => {
    setMappingPattern(pattern);
    setMapModalOpen(true);
  };

  // アコーディオンスタイル
  const accordionStyles = {
    root: { border: "none" },
    item: {
      border: "1px solid var(--mantine-color-default-border)",
      borderRadius: "var(--mantine-radius-md)",
      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      marginBottom: "12px",
      backgroundColor: "light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))",
      overflow: "hidden",
      "&:hover": {
        borderColor: "var(--mantine-color-blue-5)",
      },
      "&[data-active=true]": {
        borderColor: "var(--mantine-color-blue-filled)",
        borderLeft: "6px solid var(--mantine-color-blue-filled)",
        boxShadow: "var(--mantine-shadow-md)",
        transform: "translateY(-2px)",
      },
    },
    control: {
      padding: "16px 20px",
      "&:focus, &:active": { outline: "none" },
      "&[data-active]": {
        backgroundColor: "light-dark(var(--mantine-color-blue-0), var(--mantine-color-dark-5))",
        "& .item-title": {
          color: "light-dark(var(--mantine-color-blue-filled), var(--mantine-color-blue-4))",
        },
      },
    },
    content: {
      padding: "0 20px 20px 20px",
      backgroundColor: "light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))",
    },
  };

  return (
    <>
      <Stack gap="lg" style={{ position: "relative" }}>
        {loading && partners.length === 0 ? (
          <Paper shadow="xs" p="xl" withBorder>
            <Group justify="center">
              <Loader />
            </Group>
          </Paper>
        ) : (
          <>
            {loading && (
              <Box
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 10,
                  backgroundColor: "rgba(255,255,255,0.5)",
                  borderRadius: "8px",
                }}
              >
                <Group justify="center" h="100%">
                  <Loader size="xl" />
                </Group>
              </Box>
            )}

            {/* 取引先マスタ一覧 */}
            <Paper shadow="xs" p="md" withBorder>
              <Group justify="space-between" mb="md" align="center">
                <Title order={4}>取引先マスタ ({partners.length}件)</Title>
                <Button
                  size="xs"
                  leftSection={<Plus size={14} />}
                  onClick={() => setAddModalOpen(true)}
                >
                  取引先を新規登録
                </Button>
              </Group>

              {partners.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  取引先が登録されていません
                </Text>
              ) : (
                <Accordion
                  chevronPosition="right"
                  variant="default"
                  value={openedPartnerId}
                  onChange={setOpenedPartnerId}
                  styles={accordionStyles}
                >
                  {partners.map((partner) => (
                    <PartnerAccordionItem
                      key={partner.id}
                      partner={partner}
                      customerId={customerId}
                      onUpdate={loadData}
                    />
                  ))}
                </Accordion>
              )}
            </Paper>

            {/* 未分類パターン */}
            <UnmappedPatternsSection patterns={unmappedPatterns} onMapPattern={openMapModal} />
          </>
        )}
      </Stack>

      {/* 追加モーダル */}
      <PartnerAddModal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={handleAdd}
      />

      {/* 紐付けモーダル */}
      <PatternMapModal
        opened={mapModalOpen}
        onClose={() => {
          setMapModalOpen(false);
          setMappingPattern(null);
        }}
        pattern={mappingPattern}
        partners={partners}
        onMap={handleMapPattern}
      />
    </>
  );
}
